import React, { useState, useEffect } from "react";
import { Drawer, Form, Input, Radio, Select } from "antd";
import { getRole } from "../../helpers";
import "./style.css";

const isDiagnosticAdmin = () => {
  const role = getRole();
  return role === "DIAGNOSTIC_ADMIN" || role === "DIAGNOSTIC_REVIEWER";
};

const ProductDrawer = ({
  visible,
  onClose,
  onSubmit,
  isLoading,
  editMode = false,
  initialValues = null
}) => {
  const [form] = Form.useForm();
  const [formValid, setFormValid] = useState(false);
  const diagnosticOnly = isDiagnosticAdmin();

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      onSubmit(values);
      if (!editMode) {
        form.resetFields();
        setFormValid(false);
      }
    });
  };

  // Watch form values using the correct syntax
  const formValues = Form.useWatch([], form);

  useEffect(() => {
    if (!formValues) return;

    form
      .validateFields({ validateOnly: true })
      .then(() => {
        setFormValid(true);
      })
      .catch(() => {
        setFormValid(false);
      });
  }, [formValues, form]);

  // Reset form validation when drawer opens/closes
  useEffect(() => {
    if (visible) {
      setFormValid(false);
      if (editMode && initialValues) {
        form.setFieldsValue(initialValues);
      } else if (!editMode) {
        form.resetFields();
        if (diagnosticOnly) {
          form.setFieldsValue({ category: "Diagnostics" });
        }
      }
    }
  }, [visible, editMode, initialValues, form, diagnosticOnly]);

  return (
    <Drawer
      title={editMode ? "Edit Product" : "Add Product"}
      placement="right"
      closable={true}
      onClose={() => {
        onClose();
        if (!editMode) {
          form.resetFields();
          setFormValid(false);
        }
      }}
      open={visible}
      width={800}
      className="product-drawer"
      footer={
        <div className="drawer-footer">
          <button
            className="cancel-btn"
            onClick={() => {
              onClose();
              if (!editMode) {
                form.resetFields();
                setFormValid(false);
              }
            }}
          >
            CANCEL
          </button>
          <button
            className={`submit-btn ${!formValid ? 'submit-btn-disabled' : ''}`}
            onClick={handleSubmit}
            disabled={!formValid || isLoading}
          >
            {editMode ? "UPDATE PRODUCT" : "ADD PRODUCT"}
          </button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        className="product-form"
        initialValues={{
          product_name: '',
          therapy_area: undefined,
          category: diagnosticOnly ? 'Diagnostics' : undefined
        }}
      >
        <div className="form-fields">
          <Form.Item
            name="product_name"
            label={<>Product Name</>}
            rules={[{ required: true, message: "Please enter product name!" }]}
          >
            <Input placeholder="Product Name" />
          </Form.Item>

          <Form.Item
            name="therapy_area"
            label={<>Therapy Area</>}
            rules={[{ required: true, message: "Please select therapy area!" }]}
          >
            <Select placeholder="Please select a therapy area">
              <Select.Option value="HIV">HIV</Select.Option>
              <Select.Option value="HCV">HCV</Select.Option>
              <Select.Option value="HBV">HBV</Select.Option>
              <Select.Option value="Covid-19">Covid-19</Select.Option>
              <Select.Option value="Cancer">Cancer</Select.Option>
              <Select.Option value="Malaria">Malaria</Select.Option>
            </Select>
          </Form.Item>
        </div>

        {diagnosticOnly ? (
          <Form.Item name="category" hidden>
            <Input type="hidden" />
          </Form.Item>
        ) : (
          <Form.Item
            name="category"
            label={<>Category</>}
            rules={[{ required: true, message: "Please select category!" }]}
          >
            <Radio.Group disabled={editMode}>
              <Radio value="FDF">FDF</Radio>
              <Radio value="API">API</Radio>
              <Radio value="Diagnostics">Diagnostics</Radio>
            </Radio.Group>
          </Form.Item>
        )}

        <Form.Item
          label={diagnosticOnly ? <>Diagnostics stages</> : <>Stages:</>}
        >
          <div className={`stages-section ${diagnosticOnly ? "stages-section--diagnostics-only" : ""}`}>
            {!diagnosticOnly && (
            <div className="fdf-stages">
              <div className="stage-sub-label">FDF</div>
              <div className="stage-box">Development set-up</div>
              <div className="stage-box">Trial Batch</div>
              <div className="stage-box">Pilot BE</div>
              <div className="stage-box">Exhibit batches</div>
              <div className="stage-box">Pivotal BE</div>
              <div className="stage-box">Stability loading in different packs</div>
              <div className="stage-box">Stability data 6 months</div>
              <div className="stage-box">Dossier Compilation</div>
              <div className="stage-box">DF Dossier Filing USFDA</div>
              <div className="stage-box">DF Dossier Filing WHO</div>
              <div className="stage-box">USFDA Approval (FDF)</div>
              <div className="stage-box">WHO-PQ Approval (FDF)</div>
            </div>
            )}
            {!diagnosticOnly && (
            <div className="api-stages">
              <div className="stage-sub-label">API</div>
              <div className="stage-box">Investigation and route selection</div>
              <div className="stage-box">R&D Batch API</div>
              <div className="stage-box">Lab scale batch API</div>
              <div className="stage-box">RM Procurement</div>
              <div className="stage-box">API Trial Batch</div>
              <div className="stage-box">API Validation Batches</div>
              <div className="stage-box">API Stability loading</div>
              <div className="stage-box">API Stability Study</div>
              <div className="stage-box">DMF Compilation</div>
              <div className="stage-box">DMF Filing USFDA</div>
              <div className="stage-box">DMF Filing WHO-PQ</div>
              <div className="stage-box">USFDA Approval (API)</div>
              <div className="stage-box">WHO-PQ Approval (API)</div>
            </div>
            )}
            <div className="diagnostics-stages">
              {!diagnosticOnly && <div className="stage-sub-label">Diagnostics</div>}
              <div className="stage-box">Facility and equipment IQ, OQ, and PQ</div>
              <div className="stage-box">GMP certification</div>
              <div className="stage-box">ISO13485 certification</div>
              <div className="stage-box">Validation batches for regulatory dossier submission</div>
              <div className="stage-box">Analytical studies for regulatory dossier submission</div>
              <div className="stage-box">Accelerated stability studies for regulatory dossier submission</div>
              <div className="stage-box">Real time stability studies for regulatory dossier submission</div>
              <div className="stage-box">Clinical studies for regulatory dossier submission</div>
              <div className="stage-box">Dossier filing - Local</div>
              <div className="stage-box">Dossier filing - ERPD</div>
              <div className="stage-box">Dossier filing - WHO PQ</div>
              <div className="stage-box">Local approval</div>
              <div className="stage-box">ERPD approval</div>
              <div className="stage-box">WHO PQ approval</div>
              <div className="stage-box">First production batch</div>
            </div>
          </div>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default ProductDrawer;

